/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ChatHeader as CdsLabsChatHeader } from '@coursera-web/cds-labs';

import { useRetracked } from 'js/lib/retracked';

import { Grid, Tag } from '@coursera/cds-core';
import {
  AIGenerateBrandedNewIcon,
  CloseIcon,
  ExternalLinkIcon,
  MaximizeIcon,
  MinimizeIcon,
  NewChatIcon,
  SearchIcon,
  SettingsIcon,
  SwapIcon,
} from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachBrandLabel } from 'bundles/ai-coach-platform/components/branding/CoachBranding';
import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useCdsLabsChatComponents } from 'bundles/ai-coach-platform/utils/statsigFeatureFlags';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

export type ChatPopupButton = 'in' | 'out' | undefined;

export type Props = {
  onClose: () => void;
  showClose: boolean;
  showStartNewChat?: boolean;
  showSettings?: boolean;
  handleNewSession?: () => void;
  handleSettingsClick?: () => void;
  loading?: boolean;
  isCoachStudio?: boolean;
  showFullScreenButton?: boolean;
  toggleFullScreen?: () => void;
  isFullScreen?: boolean;
  showPositionToggle?: boolean;
  handlePositionToggleClick?: () => void;
  chatPopupButton?: ChatPopupButton;
  handlePopupButtonClick?: () => void;
  showSearchButton?: boolean;
  searchButtonRef?: React.RefObject<HTMLButtonElement>;
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  enableContinuousChat?: boolean;
};

/**
 * Default header component for Coach chat pattern
 */
const ChatHeader = (props: Props) => {
  const {
    onClose,
    handleSettingsClick,
    showSettings,
    showClose,
    showStartNewChat = true,
    handleNewSession,
    loading = false,
    isCoachStudio = false,
    showFullScreenButton = false,
    toggleFullScreen,
    isFullScreen = false,
    showPositionToggle = false,
    handlePositionToggleClick,
    chatPopupButton,
    handlePopupButtonClick,
    showSearchButton = false,
    searchButtonRef,
    settingsButtonRef,
    enableContinuousChat = false,
  } = props;
  const trackRetracked = useRetracked();
  const trackingData = useCoachTrackingContext()?.data;
  const track = useTracker();

  const { enterSearchMode, isBrandUpdateEnabled } = useCoachChatContext();
  const isCdsLabsEnabled = useCdsLabsChatComponents();

  const handleCoachSettingsClick = () => {
    handleSettingsClick?.();

    track('interact_coach', {
      coachInteractionType: 'coach_settings_open',
      coach: {
        mode: trackingData?.mode,
      },
    });
  };

  const handleNewSessionClick = () => {
    if (!enableContinuousChat) {
      // V2 eventing
      trackRetracked({
        trackingData: {
          timestamp: Date.now(),
        },
        trackingName: 'ai_course_coach_chat_header_new_session_button',
        action: `click`,
      });

      // V3 eventing
      track('interact_coach', {
        coachInteractionType: 'start_new_chat_session',
        coach: {
          mode: trackingData?.mode,
        },
        ...(trackingData?.courseId && {
          product: {
            type: 'course',
            id: trackingData.courseId as string,
          },
        }),
        ...(trackingData?.itemId && {
          courseItem: {
            id: trackingData.itemId as string,
          },
        }),
      });
    }

    handleNewSession?.();
  };

  const fullScreenButtonLabel = isFullScreen ? _t('Collapse') : _t('Expand');

  const handleCloseClick = () => {
    trackRetracked({
      trackingData: { timestamp: Date.now() },
      trackingName: 'ai_course_coach_chat_header_close_button',
      action: 'click',
    });
    onClose();
  };

  if (isCdsLabsEnabled) {
    return (
      <CdsLabsChatHeader
        onSwapPosition={showPositionToggle ? handlePositionToggleClick : undefined}
        popupButtonMode={chatPopupButton}
        onPopupButtonClick={chatPopupButton ? handlePopupButtonClick : undefined}
        onSearch={showSearchButton ? () => enterSearchMode() : undefined}
        searchButtonRef={searchButtonRef}
        onNewChat={showStartNewChat ? handleNewSessionClick : undefined}
        onSettings={showSettings ? handleCoachSettingsClick : undefined}
        settingsButtonRef={settingsButtonRef}
        onToggleFullScreen={showFullScreenButton ? toggleFullScreen : undefined}
        isFullScreen={isFullScreen}
        onClose={showClose ? handleCloseClick : undefined}
        loading={loading}
        showFade
      />
    );
  }

  return (
    <Grid
      container
      className="ai-coach-chat-header"
      justifyContent="space-between"
      css={css`
        padding: 0 var(--cds-spacing-200);
        height: 54px;
        align-items: center;
      `}
    >
      <Grid
        item
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {isBrandUpdateEnabled ? <AIGenerateBrandedNewIcon size="large" /> : <CoachBrandLabel />}
        {isCoachStudio && (
          <Tag
            variant="status"
            priority="primary"
            color="highlight1"
            css={css`
              margin-left: var(--cds-spacing-50);
            `}
          >
            STUDIO
          </Tag>
        )}
      </Grid>
      <Grid
        item
        css={css`
          display: flex;
          gap: 4px;
        `}
      >
        {showPositionToggle && (
          <CoachIconButton
            size="small"
            aria-label={_t('Swap the popup position between left and right')}
            tooltip={_t('Swap the popup position between left and right')}
            tooltipPlacement="bottom"
            onClick={() => {
              handlePositionToggleClick?.();
            }}
            data-testid="coach-swap-position"
            icon={
              <SwapIcon
                css={css`
                  transform: rotate(90deg);
                  color: var(--cds-color-grey-950);
                `}
                color="default"
              />
            }
            disabled={loading}
          />
        )}
        {chatPopupButton && (
          <CoachIconButton
            size="small"
            aria-label={
              chatPopupButton === 'in' ? _t('Pop-in the chat into the page') : _t('Pop-out the chat into a popup')
            }
            tooltip={
              chatPopupButton === 'in' ? _t('Pop-in the chat into the page') : _t('Pop-out the chat into a popup')
            }
            tooltipPlacement="bottom"
            onClick={handlePopupButtonClick}
            data-testid={chatPopupButton === 'in' ? 'coach-pop-in' : 'coach-pop-out'}
            icon={<ExternalLinkIcon color="default" />}
            disabled={loading}
          />
        )}
        {showSearchButton && (
          <CoachIconButton
            ref={searchButtonRef}
            aria-label={_t('Find in chat')}
            size="small"
            icon={<SearchIcon color="default" />}
            tooltip={_t('Find in chat')}
            tooltipPlacement="bottom"
            onClick={() => {
              enterSearchMode();
            }}
            disabled={loading}
          />
        )}
        {showStartNewChat && (
          <CoachIconButton
            size="small"
            aria-label={_t('Start a new chat. This will clear your chat history so you can start fresh.')}
            tooltip={_t('Start a new chat. This will clear your chat history so you can start fresh.')}
            tooltipPlacement="bottom"
            onClick={handleNewSessionClick}
            data-testid="coach-new-chat"
            icon={<NewChatIcon color="default" />}
            disabled={loading}
          />
        )}
        {showSettings && (
          <CoachIconButton
            size="small"
            icon={<SettingsIcon color="default" />}
            aria-label={isBrandUpdateEnabled ? _t('Coursera AI Chat Settings') : _t('Coach settings')}
            tooltip={_t('Settings')}
            tooltipPlacement="bottom"
            data-testid="coach-settings"
            onClick={handleCoachSettingsClick}
            disabled={loading}
            ref={settingsButtonRef}
          />
        )}
        {showFullScreenButton && (
          <CoachIconButton
            size="small"
            aria-label={fullScreenButtonLabel}
            tooltip={fullScreenButtonLabel}
            tooltipPlacement="bottom"
            onClick={() => {
              toggleFullScreen?.();
            }}
            icon={isFullScreen ? <MinimizeIcon color="default" /> : <MaximizeIcon color="default" />}
          />
        )}
        {showClose && (
          <CoachIconButton
            size="small"
            icon={<CloseIcon color="default" />}
            aria-label={_t('Close chat')}
            tooltip={_t('Close chat')}
            tooltipPlacement="bottom"
            onClick={() => {
              trackRetracked({
                trackingData: {
                  timestamp: Date.now(),
                },
                trackingName: 'ai_course_coach_chat_header_close_button',
                action: `click`,
              });
              onClose();
            }}
            data-testid="coach-close"
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ChatHeader;
